<template>
  <div class="tutorial-host">
    <div class="space-y-6">
      <!-- Host Configuration Demo -->
      <div class="bg-white rounded-lg border border-gray-200 p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:server-20-solid" class="w-5 h-5 text-purple-600" />
          Host Configuration
        </h4>

        <!-- Simulated Host Form -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Room Name (Optional)</label>
            <input
              v-model="roomName"
              type="text"
              placeholder="Enter a room name..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Connection Features</label>
            <div class="grid grid-cols-2 gap-3">
              <label class="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                <input type="checkbox" v-model="features.video" class="text-purple-600" />
                <IconifyIcon icon="heroicons:video-camera-20-solid" class="w-5 h-5 text-gray-600" />
                <span class="text-sm">Video</span>
              </label>
              <label class="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                <input type="checkbox" v-model="features.audio" class="text-purple-600" />
                <IconifyIcon icon="heroicons:speaker-wave-20-solid" class="w-5 h-5 text-gray-600" />
                <span class="text-sm">Audio</span>
              </label>
              <label class="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                <input type="checkbox" v-model="features.chat" class="text-purple-600" />
                <IconifyIcon icon="heroicons:chat-bubble-left-right-20-solid" class="w-5 h-5 text-gray-600" />
                <span class="text-sm">Chat</span>
              </label>
              <label class="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                <input type="checkbox" v-model="features.files" class="text-purple-600" />
                <IconifyIcon icon="heroicons:document-20-solid" class="w-5 h-5 text-gray-600" />
                <span class="text-sm">Files</span>
              </label>
            </div>
          </div>

          <button
            @click="createHostConnection"
            :disabled="isCreating"
            class="w-full bg-purple-600 text-white py-3 px-4 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <IconifyIcon v-if="isCreating" icon="heroicons:arrow-path-20-solid" class="w-5 h-5 animate-spin" />
            <IconifyIcon v-else icon="heroicons:plus-20-solid" class="w-5 h-5" />
            {{ isCreating ? 'Creating Connection...' : 'Create Host Connection' }}
          </button>
        </div>
      </div>

      <!-- Simulated QR Code Display -->
      <div v-if="showQRCode" class="bg-white rounded-lg border border-gray-200 p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:qrcode-20-solid" class="w-5 h-5 text-indigo-600" />
          Example Connection QR Code
          <span class="text-xs font-normal text-gray-500">(illustration, not a real invitation)</span>
        </h4>

        <div class="flex flex-col items-center space-y-4">
          <!-- Simulated QR Code -->
          <div class="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
            <div class="text-center">
              <IconifyIcon icon="tabler:qr-code" class="w-16 h-16 text-gray-400 mx-auto mb-2" />
              <p class="text-sm text-gray-600">QR Code would appear here</p>
              <p class="text-xs text-gray-500">In real app: Interactive QR code</p>
            </div>
          </div>

          <!-- Connection Info -->
          <div class="w-full bg-gray-50 rounded-lg p-4">
            <h5 class="font-medium text-gray-900 mb-2">Connection Details:</h5>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600">Room:</span>
                <span class="font-medium">{{ roomName || 'Random Room' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Status:</span>
                <span class="text-green-600 font-medium">Waiting for clients...</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Features:</span>
                <span class="font-medium">{{ activeFeaturesCount }} enabled</span>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-2 w-full">
            <button
              @click="copyConnectionLink"
              class="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <IconifyIcon icon="heroicons:clipboard-document-20-solid" class="w-4 h-4" />
              Copy Link
            </button>
            <button
              @click="shareConnection"
              class="flex-1 bg-indigo-100 text-indigo-700 py-2 px-4 rounded-md hover:bg-indigo-200 transition-colors flex items-center justify-center gap-2"
            >
              <IconifyIcon icon="heroicons:share-20-solid" class="w-4 h-4" />
              Share
            </button>
          </div>
        </div>
      </div>

      <!-- Instructions -->
      <div class="bg-blue-50 rounded-lg p-4">
        <h5 class="font-medium text-blue-900 mb-2 flex items-center gap-2">
          <IconifyIcon icon="heroicons:light-bulb-20-solid" class="w-5 h-5" />
          How it works:
        </h5>
        <ol class="list-decimal list-inside space-y-1 text-sm text-blue-800">
          <li>Your browser creates a WebRTC connection</li>
          <li>Connection details are encoded into a QR code</li>
          <li>Other devices scan the QR code to join</li>
          <li>Direct P2P connection is established</li>
          <li>No servers needed - it's all browser-to-browser!</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import IconifyIcon from '../IconifyIcon.vue'

// Props
const props = defineProps<{
  onNext?: () => void
}>()

// Reactive state
const roomName = ref('Demo Room')
const isCreating = ref(false)
const showQRCode = ref(false)
const features = ref({
  video: true,
  audio: true,
  chat: true,
  files: false
})

// Computed
const activeFeaturesCount = computed(() => {
  return Object.values(features.value).filter(Boolean).length
})

// Methods
const createHostConnection = async () => {
  isCreating.value = true

  // Simulate connection creation
  await new Promise(resolve => setTimeout(resolve, 2000))

  isCreating.value = false
  showQRCode.value = true

  // Auto-advance to next step after showing QR code
  setTimeout(() => {
    if (props.onNext) {
      props.onNext()
    }
  }, 3000)
}

// These used to copy an invented 'webrtc-qr://demo-room-<random>' link that
// nothing can open, and "share" only logged to the console. Both now hand out
// the real page where a working chat can be started.
const realChatUrl = () => `${window.location.origin}/chat`

const copyConnectionLink = async () => {
  try {
    await navigator.clipboard.writeText(realChatUrl())
  } catch (e) {
    console.error('Copy failed:', e)
  }
}

const shareConnection = async () => {
  const url = realChatUrl()
  if (navigator.share) {
    try {
      await navigator.share({ title: 'WebRTC chat', url })
      return
    } catch {
      // user cancelled or share failed: fall back to copying
    }
  }
  await copyConnectionLink()
}
</script>

<style scoped>
.tutorial-host {
  /* Component-specific styles */
}
</style>